const POLY = { triangle: 3, square: 4, pentagon: 5, hexagon: 6, octagon: 8 };

function poly(n, r = 40, rot = -90) {
  return Array.from({ length: n }, (_, i) => {
    const a = ((rot + (360 / n) * i) * Math.PI) / 180;
    return `${50 + r * Math.cos(a)},${50 + r * Math.sin(a)}`;
  }).join(" ");
}

export function Shape({ shape, color = "#38bdf8", size = 64 }) {
  const s = { fill: color, stroke: "rgba(0,0,0,0.25)", strokeWidth: 3 };
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-label={shape} role="img">
      {shape === "circle" && <circle cx="50" cy="50" r="40" {...s} />}
      {shape === "oval" && <ellipse cx="50" cy="50" rx="44" ry="28" {...s} />}
      {shape === "rectangle" && <rect x="8" y="25" width="84" height="50" rx="4" {...s} />}
      {shape === "square" && <rect x="14" y="14" width="72" height="72" rx="4" {...s} />}
      {POLY[shape] && shape !== "square" && <polygon points={poly(POLY[shape], 42, shape === "octagon" ? -67.5 : -90)} {...s} />}
      {shape === "star" && <polygon points="50,6 61,38 95,38 67,58 78,92 50,72 22,92 33,58 5,38 39,38" {...s} />}
      {shape === "heart" && <path d="M50 88 C10 60 5 30 25 18 C38 10 48 18 50 28 C52 18 62 10 75 18 C95 30 90 60 50 88Z" {...s} />}
    </svg>
  );
}

const COIN = { penny: ["#c47a3a", 38, "1¢"], nickel: ["#b8bcc2", 44, "5¢"], dime: ["#cfd3d8", 34, "10¢"], quarter: ["#c9cdd2", 50, "25¢"] };
export function Coin({ coin, labelled = true }) {
  const [c, d, lab] = COIN[coin];
  return (
    <svg viewBox="0 0 60 60" width={d + 16} height={d + 16} role="img" aria-label={coin}>
      <circle cx="30" cy="30" r={d / 2} fill={c} stroke="rgba(0,0,0,0.35)" strokeWidth="2" />
      <circle cx="30" cy="30" r={d / 2 - 4} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
      {labelled && <text x="30" y="35" textAnchor="middle" fontSize="13" fontWeight="800" fill="#1f2937">{lab}</text>}
    </svg>
  );
}

export function Clock({ h, m, size = 170 }) {
  const ma = (m / 60) * 360;
  const ha = ((h % 12) / 12) * 360 + (m / 60) * 30;
  const hand = (deg, len, w, c) => <line x1="50" y1="50" x2={50 + len * Math.sin((deg * Math.PI) / 180)} y2={50 - len * Math.cos((deg * Math.PI) / 180)} stroke={c} strokeWidth={w} strokeLinecap="round" />;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label="clock">
      <circle cx="50" cy="50" r="46" fill="#fff" stroke="#0f172a" strokeWidth="3" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = ((i + 1) * 30 * Math.PI) / 180;
        return <text key={i} x={50 + 36 * Math.sin(a)} y={54 + -36 * Math.cos(a)} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0f172a">{i + 1}</text>;
      })}
      {hand(ha, 22, 4.5, "#0f172a")}
      {hand(ma, 34, 2.5, "#ec4899")}
      <circle cx="50" cy="50" r="3" fill="#0f172a" />
    </svg>
  );
}

function Pizza({ n, k }) {
  const slices = Array.from({ length: n }, (_, i) => {
    const a0 = (i / n) * 2 * Math.PI - Math.PI / 2;
    const a1 = ((i + 1) / n) * 2 * Math.PI - Math.PI / 2;
    const p = (a) => `${50 + 44 * Math.cos(a)},${50 + 44 * Math.sin(a)}`;
    return <path key={i} d={`M50,50 L${p(a0)} A44,44 0 ${n === 1 ? 1 : 0} 1 ${p(a1)} Z`} fill={i < k ? "#f97316" : "#fde68a"} stroke="#92400e" strokeWidth="2" />;
  });
  return (
    <svg viewBox="0 0 100 100" width="170" height="170" role="img" aria-label={`pizza with ${k} of ${n} slices topped`}>
      <circle cx="50" cy="50" r="48" fill="#d97706" />{slices}
      {Array.from({ length: k }, (_, i) => { const a = ((i + 0.5) / n) * 2 * Math.PI - Math.PI / 2; return <circle key={`p${i}`} cx={50 + 26 * Math.cos(a)} cy={50 + 26 * Math.sin(a)} r="5" fill="#b91c1c" />; })}
    </svg>
  );
}

const Emojis = ({ e, n }) => <div className="flex max-w-xs flex-wrap justify-center gap-1 text-4xl">{Array.from({ length: n }, (_, i) => <span key={i} className="learn-pop" style={{ animationDelay: `${i * 0.04}s` }}>{e}</span>)}</div>;

export function Visual({ v }) {
  if (!v) return null;
  const box = "flex items-center justify-center";
  switch (v.kind) {
    case "critters": return <div className={box} data-testid="visual-critters"><Emojis e={v.emoji} n={v.n} /></div>;
    case "groups": return <div className="grid grid-cols-2 gap-3" data-testid="visual-groups">{[v.a, v.b].map((n, i) => <div key={i} className="rounded-3xl bg-white/10 p-3"><p className="mb-1 text-center text-xs font-black uppercase text-q-muted">{i ? "Right" : "Left"}</p><Emojis e={v.emoji} n={n} /></div>)}</div>;
    case "sequence": return <div className="flex flex-wrap justify-center gap-2" data-testid="visual-sequence">{v.items.map((x, i) => <span key={i} className={`flex h-14 min-w-[56px] items-center justify-center rounded-2xl px-2 font-display text-2xl font-extrabold ${x === "?" ? "border-2 border-dashed border-[rgb(var(--glow-kids))] text-[rgb(var(--glow-kids))]" : "bg-white/10 text-q-text"}`}>{x}</span>)}</div>;
    case "pizza": return <div className={box} data-testid="visual-pizza"><Pizza n={v.n} k={v.k} /></div>;
    case "emoji": return <div className={`${box} flex-col`} data-testid="visual-emoji"><span className="learn-pop text-7xl">{v.emoji}</span>{v.word && <span className="mt-1 font-display text-lg font-bold text-q-muted">{v.word}</span>}</div>;
    case "flag": return <div className={box} data-testid="visual-flag"><span className="learn-pop text-[96px] leading-none">{v.emoji}</span></div>;
    case "clock": return <div className={box} data-testid="visual-clock"><Clock h={v.h} m={v.m} /></div>;
    case "coins": return <div className="flex flex-wrap items-center justify-center gap-1" data-testid="visual-coins">{v.coins.map((c, i) => <Coin key={i} coin={c} labelled={false} />)}</div>;
    case "shape": return <div className={box} data-testid="visual-shape"><Shape shape={v.shape} color={v.color} size={110} /></div>;
    case "shapes_row": return <div className="flex items-center justify-center gap-2" data-testid="visual-shapes-row">{v.items.map((s, i) => (s === "?" ? <span key={i} className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-dashed border-[rgb(var(--glow-kids))] font-display text-3xl text-[rgb(var(--glow-kids))]">?</span> : <Shape key={i} shape={s} color="#f472b6" />))}</div>;
    case "sentence": return <p className="text-center font-display text-xl text-q-text" data-testid="visual-sentence">{v.text}</p>;
    default: return null;
  }
}

export function ChoiceFace({ c }) {
  const v = c.visual;
  if (v?.kind === "color") return <span className="flex flex-col items-center gap-1"><span className="h-12 w-12 rounded-full border-2 border-black/20" style={{ background: v.hex }} /><span>{c.label}</span></span>;
  if (v?.kind === "shape") return <span className="flex flex-col items-center gap-1"><Shape shape={v.shape} color={v.color} size={48} /><span className="text-sm">{c.label}</span></span>;
  return c.label;
}
